import { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE, useAuth } from "../components/AuthContext";
import { Link } from "react-router-dom";
import { ArrowUpRight, Briefcase, Clock, Users, Sparkles } from "lucide-react";
import OnboardingWizard from "../components/OnboardingWizard";
import IdCheckStatsCard from "../components/IdCheckStatsCard";

const STATUS_LABEL = { todo: "To do", in_progress: "In progress", review: "Review", done: "Done" };
const STATUS_COLOR = { todo: "var(--ink-3)", in_progress: "var(--accent)", review: "var(--moss)", done: "var(--ink)" };

function fmtHM(mins) {
  const h = Math.floor(mins / 60), m = mins % 60;
  return h ? `${h}h ${m}m` : `${m}m`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function Dashboard() {
  const { state } = useAuth();
  const [data, setData] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [insights, setInsights] = useState([]);
  const [showWizard, setShowWizard] = useState(false);

  useEffect(() => {
    axios.get(`${API_BASE}/dashboard`).then((r) => setData(r.data)).catch(() => {});
    axios.get(`${API_BASE}/reports/funnel?days=90`).then((r) => setFunnel(r.data)).catch(() => {});
    axios.get(`${API_BASE}/insights/proposals?status=pending&limit=3`)
      .then((r) => setInsights(r.data)).catch(() => {});
  }, []);

  // First-login accounting wizard trigger: owner/manager, not dismissed, no connection yet.
  useEffect(() => {
    const u = state?.user;
    if (!u) return;
    if (!["owner", "manager"].includes(u.role)) return;
    if (u.onboarding_accounting_dismissed_at) return;
    let alive = true;
    axios.get(`${API_BASE}/accounting/connections`)
      .then((r) => { if (alive && (r.data || []).length === 0) setShowWizard(true); })
      .catch(() => {});
    return () => { alive = false; };
  }, [state?.user]);

  if (!data) return <div className="p-12 text-[var(--ink-2)]" data-testid="dashboard-loading">Loading…</div>;

  const week = (data.week_minutes.billable || 0) + (data.week_minutes.nonbillable || 0);
  const billPct = week ? Math.round((data.week_minutes.billable / week) * 100) : 0;

  return (
    <div className="px-10 py-12 max-w-[1240px]" data-testid="dashboard-page">
      <div className="overline mb-2">Dashboard · {state?.org?.name}</div>
      <h1 className="font-heading text-4xl mb-2" style={{ fontWeight: 500 }}>
        Good to see you, <em className="italic" style={{ color: "var(--accent)" }}>{state?.user?.name.split(" ")[0]}</em>.
      </h1>
      <p className="text-[var(--ink-2)] mb-10 text-lg">Here's how the firm looked this week.</p>

      <StatRow data={data} week={week} billPct={billPct} funnel={funnel} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-10">
        <UpcomingJobs jobs={data.upcoming_jobs} />
        <PipelineSummary byStatus={data.jobs_by_status} total={data.jobs_total} />
      </div>

      <IdCheckStatsCard />

      <AIInsightsPanel insights={insights} />

      {showWizard && (
        <OnboardingWizard onDismiss={() => setShowWizard(false)} />
      )}
    </div>
  );
}

function StatRow({ data, week, billPct, funnel }) {
  const pipelineValue = funnel?.pipeline_value || 0;
  const pipelineCount = funnel?.pipeline_count || 0;
  const pipelineFmt = pipelineValue >= 1000
    ? `$${(pipelineValue / 1000).toFixed(pipelineValue >= 10000 ? 0 : 1)}k`
    : `$${pipelineValue.toFixed(0)}`;
  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-[var(--border)] border border-[var(--border)] rounded-md overflow-hidden">
      <Stat icon={<Users size={14} />} label="Clients" value={data.clients_count} testid="dashboard-stat-clients" />
      <Stat icon={<Briefcase size={14} />} label="Open Jobs" value={data.jobs_total - (data.jobs_by_status.done || 0)} sub={`${data.jobs_by_status.done || 0} done`} testid="dashboard-stat-open-jobs" />
      <Stat icon={<Clock size={14} />} label="Logged · 7 days" value={fmtHM(week)} sub={`${billPct}% billable`} testid="dashboard-stat-week-hours" />
      <Stat icon={<Briefcase size={14} />} label="In Progress" value={data.jobs_by_status.in_progress || 0} sub="active jobs" testid="dashboard-stat-in-progress" />
      <Stat icon={<ArrowUpRight size={14} />} label="Pipeline value" value={pipelineFmt} sub={`${pipelineCount} active leads · 90d`} testid="dashboard-stat-pipeline-value" />
    </div>
  );
}

function UpcomingJobs({ jobs }) {
  return (
    <div className="lg:col-span-2 card p-6" data-testid="dashboard-upcoming">
      <div className="flex items-center justify-between mb-5">
        <div className="overline">Upcoming · top 5 by due date</div>
        <Link to="/jobs" className="text-[12px] text-[var(--accent)] hover:underline inline-flex items-center gap-1">
          View all jobs <ArrowUpRight size={12} />
        </Link>
      </div>
      {jobs.length === 0 ? (
        <div className="text-[var(--ink-3)] text-sm">Nothing on the radar.</div>
      ) : (
        <div className="space-y-2">
          {jobs.map((j) => (
            <div key={j.job_id} className="flex items-center gap-4 py-3 border-b border-[var(--border)] last:border-0">
              <span className="badge" style={{ background: STATUS_COLOR[j.status], color: "var(--paper)" }}>
                {STATUS_LABEL[j.status]}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[14px] text-[var(--ink)] font-medium truncate">{j.title}</div>
                <div className="text-[12px] text-[var(--ink-3)] font-mono">Due {fmtDate(j.due_at)} · budget {j.budget_minutes}m</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PipelineSummary({ byStatus, total }) {
  return (
    <div className="card p-6" data-testid="dashboard-pipeline">
      <div className="overline mb-5">Pipeline</div>
      <div className="space-y-4">
        {Object.entries(byStatus).map(([s, n]) => {
          const pct = total ? (n / total) * 100 : 0;
          return (
            <div key={s}>
              <div className="flex justify-between text-[12px] mb-1.5">
                <span className="text-[var(--ink-2)]">{STATUS_LABEL[s]}</span>
                <span className="font-mono text-[var(--ink)]">{n}</span>
              </div>
              <div className="h-2 bg-[var(--subtle)] rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: STATUS_COLOR[s] }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AIInsightsPanel({ insights }) {
  return (
    <div className="card p-6 mt-6" data-testid="dashboard-insights">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Sparkles size={14} style={{ color: "var(--accent)" }} />
          <div className="overline">AI Insights · top {insights.length || 3}</div>
        </div>
        <Link to="/insights" className="text-[12px] text-[var(--accent)] hover:underline inline-flex items-center gap-1" data-testid="dashboard-insights-link">
          Open insights <ArrowUpRight size={12} />
        </Link>
      </div>
      {insights.length === 0 ? (
        <div className="text-[var(--ink-3)] text-sm">
          No pending proposals. <Link to="/insights" className="underline text-[var(--accent)]">Run a scan</Link> to surface anomalies and draft jobs.
        </div>
      ) : (
        <div className="space-y-3">
          {insights.map((p) => (
            <Link
              key={p.proposal_id}
              to="/insights"
              data-testid={`dashboard-insight-${p.proposal_id}`}
              className="block py-3 border-b border-[var(--border)] last:border-0 hover:bg-[var(--subtle)] -mx-2 px-2 rounded transition"
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="badge" style={{ background: "var(--accent)", color: "var(--paper)" }}>{p.kind.replace("_", " ")}</span>
              </div>
              <div className="text-[14px] text-[var(--ink)] font-medium leading-snug">{p.title}</div>
              <div className="text-[12px] text-[var(--ink-3)] mt-0.5 line-clamp-2">{p.body.slice(0, 160)}{p.body.length > 160 ? "…" : ""}</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ icon, label, value, sub, testid }) {
  return (
    <div className="bg-[var(--paper)] p-6" data-testid={testid}>
      <div className="flex items-center gap-2 text-[var(--accent)] mb-3">{icon}<span className="overline">{label}</span></div>
      <div className="font-heading text-3xl tabular" style={{ fontWeight: 500 }}>{value}</div>
      {sub && <div className="text-[12px] text-[var(--ink-3)] mt-1">{sub}</div>}
    </div>
  );
}
